import { useState, useMemo, useCallback } from 'react';
import { CalculatorInputs, CalculatorOutputs, TimePeriod } from '../types/calculator';
import { DEFAULT_INPUTS } from '../constants/defaults';
import { calculateAll, applyMultiplier } from '../utils/calculations';

interface UseCalculatorReturn {
  inputs: CalculatorInputs;
  outputs: CalculatorOutputs;
  scaledOutputs: CalculatorOutputs;
  timePeriod: TimePeriod;
  setInput: <K extends keyof CalculatorInputs>(key: K, value: CalculatorInputs[K]) => void;
  setTimePeriod: (period: TimePeriod) => void;
  resetInputs: () => void;
}

export const useCalculator = (): UseCalculatorReturn => {
  const [inputs, setInputs] = useState<CalculatorInputs>(DEFAULT_INPUTS);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('day');

  // Calculate base outputs (daily values)
  const outputs = useMemo(() => calculateAll(inputs), [inputs]);

  // Apply time period multiplier to outputs
  const scaledOutputs = useMemo(() => {
    // Calculate dynamic multipliers based on office days per week
    const officeDays = inputs.officeDaysPerWeek;
    const dynamicMultipliers = {
      day: 1,
      week: officeDays,
      month: officeDays * 4, // 4 weeks per month
      quarter: officeDays * 12, // 12 weeks per quarter
      year: officeDays * 48, // 48 working weeks per year
    };

    const multiplier = dynamicMultipliers[timePeriod];

    return {
      employeeValue: {
        timeSavedHours: applyMultiplier(outputs.employeeValue.timeSavedHours, multiplier),
        productivityValue: applyMultiplier(outputs.employeeValue.productivityValue, multiplier),
        fteRecovered: outputs.employeeValue.fteRecovered, // FTE is already annualized
        companyInvestment: applyMultiplier(outputs.employeeValue.companyInvestment, multiplier),
        savingsPerCup: outputs.employeeValue.savingsPerCup, // Per cup value stays the same
        employeeSavings: applyMultiplier(outputs.employeeValue.employeeSavings, multiplier),
      },
      sustainability: {
        cupsSaved: applyMultiplier(outputs.sustainability.cupsSaved, multiplier),
        co2Avoided: applyMultiplier(outputs.sustainability.co2Avoided, multiplier),
        energySaved: applyMultiplier(outputs.sustainability.energySaved, multiplier),
        waterSaved: applyMultiplier(outputs.sustainability.waterSaved, multiplier),
        wasteDiverted: applyMultiplier(outputs.sustainability.wasteDiverted, multiplier),
        wasteAvoided: applyMultiplier(outputs.sustainability.wasteAvoided, multiplier),
        plasticSaved: applyMultiplier(outputs.sustainability.plasticSaved, multiplier),
        treesEquivalent: applyMultiplier(outputs.sustainability.treesEquivalent, multiplier),
        carKmOffset: applyMultiplier(outputs.sustainability.carKmOffset, multiplier),
      },
      totalCupsPerDay: applyMultiplier(outputs.totalCupsPerDay, multiplier),
    };
  }, [outputs, timePeriod, inputs.officeDaysPerWeek]);

  // Set a single input value
  const setInput = useCallback(<K extends keyof CalculatorInputs>(
    key: K,
    value: CalculatorInputs[K]
  ) => {
    setInputs((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Reset all inputs to defaults
  const resetInputs = useCallback(() => {
    setInputs(DEFAULT_INPUTS);
  }, []);

  return {
    inputs,
    outputs,
    scaledOutputs,
    timePeriod,
    setInput,
    setTimePeriod,
    resetInputs,
  };
};
